import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	AdAccountListItem,
	InstagramIntegration,
	MetaLimits,
	NeedsPageAccessAccount,
	SyncWarning
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { ConnectInstagramDialogComponent } from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/components/connect-channel/connect-instagram-dialog.component';
import { MetaInstagramAccountsService } from '@brand-lolly/app/services/meta-integrations/instagram-accounts.service';
import { extractMetaErrorMessage } from '@brand-lolly/app/shared/utils/meta-api-error.utils';
import {
	LoButton,
	LoDialogService,
	LoIcon,
	LoNotice,
	LoToastService
} from 'lo-design-system';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MetaSectionComponent } from './meta-section.component';
import { MetaIgAccountRowComponent } from './meta-ig-account-row.component';
import {
	MetaIgAccountModalComponent,
	MetaIgAccountModalData,
	MetaIgAccountModalResult
} from './meta-ig-account-modal.component';
import { MetaLimitsStripComponent } from './meta-limits-strip.component';
import { MetaNeedsPageAccessComponent } from './meta-needs-page-access.component';

@Component({
	standalone: true,
	selector: 'meta-ig-accounts-section',
	imports: [
		LoButton,
		LoIcon,
		LoNotice,
		MetaSectionComponent,
		MetaIgAccountRowComponent,
		MetaLimitsStripComponent,
		MetaNeedsPageAccessComponent
	],
	template: `
		<meta-section
			heading="Instagram accounts"
			icon="instagram_logo"
			[count]="countLabel"
			[loading]="loading"
			[skeletonRows]="3"
		>
			<button
				slot="header-action"
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				(click)="onAdd()"
			>
				<lo-icon name="lo_plus" />
				Add Instagram
			</button>
			@if (!loading && limits) {
				<meta-limits-strip
					slot="header-extra"
					[primary]="igPrimary"
					[caption]="igCaption"
					[consumed]="limits.instagram.currently_consumed"
					[max]="limits.instagram.plan_max"
				></meta-limits-strip>
			}
			@for (warn of syncWarnings; track warn.connection_id + ':' + warn.code) {
				<lo-notice
					tone="warning"
					[heading]="warn.message + ' (connection #' + warn.connection_id + ')'"
				></lo-notice>
			}
			@for (acc of accounts; track keyFor(acc)) {
				<meta-ig-account-row
					[account]="acc"
					[remainingSlots]="limits?.instagram?.remaining_slots ?? null"
					[toggleLoading]="isToggling(acc)"
					(toggle)="onToggle($event)"
					(manage)="onManage($event)"
				></meta-ig-account-row>
			}
			@if (!accounts.length) {
				<p class="empty lo-type-text-md">No Instagram accounts yet.</p>
			}
			@if (
				!loading &&
				(needsPageAccess.length ||
					(!accounts.length && hasFacebookConnection))
			) {
				<meta-needs-page-access
					[accounts]="needsPageAccess"
					[showPointer]="!accounts.length && hasFacebookConnection"
				></meta-needs-page-access>
			}
		</meta-section>
	`,
	styles: [
		`
			:host {
				display: block;

				.empty {
					margin: 0;
					color: var(--lo-color-text-secondary);
					text-align: center;
				}
			}
		`
	]
})
export class MetaIgAccountsSectionComponent {
	@Input() accounts: InstagramIntegration[] = [];
	@Input() limits: MetaLimits | null = null;
	@Input() adAccounts: AdAccountListItem[] = [];
	@Input() syncWarnings: SyncWarning[] = [];
	@Input() loading = false;
	/** Business-owned accounts discovery saw but could not connect. */
	@Input() needsPageAccess: NeedsPageAccessAccount[] = [];
	/** The pointer about Page tasks only means something once Facebook is connected. */
	@Input() hasFacebookConnection = false;

	@Output() refresh = new EventEmitter<void>();

	loadingKeys = new Set<string>();

	private destroy$ = new Subject<void>();

	private readonly dialogs = inject(LoDialogService);

	constructor(
		private dialog: MatDialog,
		private service: MetaInstagramAccountsService,
		private toasts: LoToastService
	) {}

	get countLabel(): string {
		return `${this.accounts.length} tracked`;
	}

	get igPrimary(): string {
		const ig = this.limits?.instagram;
		if (!ig) return '';
		return `${ig.currently_consumed} of ${ig.plan_max} active`;
	}

	get igCaption(): string {
		const ig = this.limits?.instagram;
		if (!ig) return '';
		const remaining = ig.remaining_slots;
		if (remaining === 0) return 'plan limit reached';
		if (remaining !== null && remaining !== undefined) {
			return `${remaining} slots free`;
		}
		return '';
	}

	onAdd(): void {
		this.dialog
			.open(ConnectInstagramDialogComponent, {
				data: {
					allowedProviders: [
						'facebook_oauth',
						'instagram_oauth',
						'password'
					],
					redirectPath:
						'user-management/integrations/integration/instagram'
				}
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				if (res?.refreshed) this.refresh.emit();
			});
	}

	onManage(account: InstagramIntegration): void {
		this.dialogs
			.open<
				MetaIgAccountModalComponent,
				MetaIgAccountModalData,
				MetaIgAccountModalResult
			>(MetaIgAccountModalComponent, {
				data: {
					account,
					limits: this.limits,
					adAccounts: this.adAccounts
				}
			})
			.closed.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				if (res?.refreshed) this.refresh.emit();
			});
	}

	onToggle(account: InstagramIntegration): void {
		const key = this.keyFor(account);
		if (this.loadingKeys.has(key)) return;
		const wasConsumed = account.is_consumed;
		this.loadingKeys = new Set([...this.loadingKeys, key]);
		const op = wasConsumed
			? account.id !== null
				? this.service.release(account.id)
				: this.service.releaseByMetaIds(this.metaIdsBody(account))
			: account.id !== null
			? this.service.consume(account.id)
			: this.service.consumeByMetaIds(this.metaIdsBody(account));
		op.pipe(takeUntil(this.destroy$)).subscribe({
			next: updated => {
				const idx = this.accounts.findIndex(
					a => this.keyFor(a) === key
				);
				if (idx !== -1) {
					this.accounts = [
						...this.accounts.slice(0, idx),
						updated,
						...this.accounts.slice(idx + 1)
					];
				}
				if (this.limits) {
					const delta = wasConsumed ? -1 : 1;
					const ig = this.limits.instagram;
					this.limits = {
						...this.limits,
						instagram: {
							...ig,
							currently_consumed: ig.currently_consumed + delta,
							remaining_slots:
								ig.remaining_slots === null
									? null
									: Math.max(0, ig.remaining_slots - delta)
						},
						currently_tracked_ig: Math.max(
							0,
							this.limits.currently_tracked_ig - delta
						)
					};
				}
				this.clearLoading(key);
			},
			error: err => {
				this.clearLoading(key);
				if (err?.status !== 402) {
					this.toasts.error(
						extractMetaErrorMessage(
							err,
							'Failed to update Instagram account'
						)
					);
				}
			}
		});
	}

	isToggling(account: InstagramIntegration): boolean {
		return this.loadingKeys.has(this.keyFor(account));
	}

	keyFor(account: InstagramIntegration): string {
		if (account.id !== null) return `id:${account.id}`;
		if (account.ig_user_id) return `igu:${account.ig_user_id}`;
		if (account.ig_business_account_id)
			return `iba:${account.ig_business_account_id}`;
		return `name:${account.ig_username}`;
	}

	private metaIdsBody(account: InstagramIntegration): {
		ig_user_id?: string;
		ig_business_account_id?: string;
	} {
		if (account.ig_user_id) return { ig_user_id: account.ig_user_id };
		if (account.ig_business_account_id)
			return { ig_business_account_id: account.ig_business_account_id };
		return {};
	}

	private clearLoading(key: string): void {
		const next = new Set(this.loadingKeys);
		next.delete(key);
		this.loadingKeys = next;
	}
}
